import { ActionModal } from '@kcftech/design-system';
import { BearingCreateRequest } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useEffect } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { isValidNumber } from 'utils/number';

const maxStringFieldLength = 50;

const requiredManufacturerErrorMessage = 'Please enter the manufacturer name';
const requiredBearingErrorMessage = 'Please enter the bearing name';

const manufacturerRules: RegisterOptions = {
    required: requiredManufacturerErrorMessage,
    maxLength: {
        value: maxStringFieldLength,
        message: `Manufacturer name cannot be greater than ${maxStringFieldLength} characters`,
    },
    validate: (value) =>
        value.trim() === '' ? requiredManufacturerErrorMessage : undefined,
};

const nameRules: RegisterOptions = {
    required: requiredBearingErrorMessage,
    maxLength: {
        value: maxStringFieldLength,
        message: `Name cannot be greater than ${maxStringFieldLength} characters`,
    },
    validate: (value) => (value.trim() === '' ? requiredBearingErrorMessage : undefined),
};

const ftfRules: RegisterOptions = {
    required: 'Please enter the fundamental train frequency',
    validate: (value) => {
        const numberValue = Number(value);
        return !isValidNumber(value) || numberValue <= 0 || numberValue >= 1
            ? 'Fundamental Train Frequency must be a number between 0 and 1'
            : undefined;
    },
};

const bsfRules: RegisterOptions = {
    required: 'Please enter the ball spin frequency',
    validate: (value) =>
        !isValidNumber(value) || Number(value) <= 1
            ? 'Ball Spin Frequency must be a number greater than 1'
            : undefined,
};

const getBpfoRules = (bpfi: number): RegisterOptions => ({
    required: 'Please enter the ball pass frequency of outer race',
    validate: (value) => {
        const num = Number(value);
        if (!isValidNumber(num) || num < 0) {
            return 'Ball Pass Frequency of Outer Race must be a positive number';
        }
        if (num <= 1 || num >= bpfi) {
            return 'Ball Pass Frequency of Outer Race must be greater than 1 and less than the value of Ball Pass Frequency of Inner Race';
        }
        return undefined;
    },
});

const getBpfiRules = (bpfo: number): RegisterOptions => ({
    required: 'Please enter the ball pass frequency of inner race',
    validate: (value) => {
        const num = Number(value);
        if (!isValidNumber(num) || num < 0) {
            return 'Ball Pass Frequency of Inner Race must be a positive number';
        }
        if (num <= bpfo) {
            return 'Ball Pass Frequency of Inner Race must be greater than the value of Ball Pass Frequency of Outer Race';
        }
        return undefined;
    },
});

const getRollerCountRules = (bpfo: number, bpfi: number): RegisterOptions => ({
    required: 'Please enter the number of rollers',
    validate: (value) => {
        if (
            !isValidNumber(value) ||
            !Number.isInteger(Number(value)) ||
            Number(value) < 0
        ) {
            return 'Number of Rollers must be a positive whole number';
        }

        if (Math.abs(value - bpfo - bpfi) > 0.1) {
            return 'Number of Rollers must be approximately equal to the sum of Ball Pass Frequency of Outer Race and Ball Pass Frequency of Inner Race';
        }

        return undefined;
    },
});

type BearingsForm = Readonly<{
    Name: string;
    Manufacturer: string;
    RollerCount: number;
    Ftf: number;
    Bsf: number;
    Bpfo: number;
    Bpfi: number;
}>;

export type BearingsModalProps = Readonly<{
    onCreate: () => void;
    onClose: () => void;
}>;

export function BearingsModal({ onCreate, onClose }: BearingsModalProps) {
    const { showToast } = useNotificationContext();

    const {
        watch,
        trigger,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<BearingsForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const { Bpfi, Bpfo, RollerCount } = watch();

    useEffect(() => {
        if (isSubmitted) {
            trigger('Bpfi');
            trigger('Bpfo');
            trigger('RollerCount');
        }
    }, [Bpfi, Bpfo, RollerCount, isSubmitted, trigger]);

    const { mutate: addBearing, isPending } = useMutation({
        mutationFn: async (request: BearingCreateRequest) =>
            sdClient.create({ body: request }),

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully added bearing',
            });

            onCreate();
        },

        onError: () => showToast({ type: 'error', content: 'Failed to add bearing' }),
    });

    const onSave = handleSubmit((formData) =>
        addBearing(
            new BearingCreateRequest({
                ...formData,
            }),
        ),
    );

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={onClose}
            title="Add New Bearing"
            actionButtonText="Add"
            closeButtonText="Cancel"
        >
            <ValidatableLabel
                label="Manufacturer"
                error={errors.Manufacturer}
                required
                className="mb-20"
            >
                <TextInput
                    autoFocus
                    placeholder="Enter manufacturer name"
                    invalid={errors.Manufacturer !== undefined}
                    {...register('Manufacturer', manufacturerRules)}
                />
            </ValidatableLabel>
            <ValidatableLabel label="Name" error={errors.Name} required className="mb-20">
                <TextInput
                    placeholder="Enter bearing name"
                    invalid={errors.Name !== undefined}
                    {...register('Name', nameRules)}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Number of Rollers"
                error={errors.RollerCount}
                required
                className="mb-20"
            >
                <TextInput
                    placeholder="Enter number of rollers"
                    invalid={errors.RollerCount !== undefined}
                    {...register('RollerCount', getRollerCountRules(Bpfo, Bpfi))}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Fundamental Train Frequency"
                error={errors.Ftf}
                required
                className="mb-20"
            >
                <TextInput
                    placeholder="Enter fundamental train frequency"
                    invalid={errors.Ftf !== undefined}
                    {...register('Ftf', ftfRules)}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Ball Spin Frequency"
                error={errors.Bsf}
                required
                className="mb-20"
            >
                <TextInput
                    placeholder="Enter ball spin frequency"
                    invalid={errors.Bsf !== undefined}
                    {...register('Bsf', bsfRules)}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Ball Pass Frequency of Outer Race"
                error={errors.Bpfo}
                required
                className="mb-20"
            >
                <TextInput
                    placeholder="Enter ball pass frequency of outer race"
                    invalid={errors.Bpfo !== undefined}
                    {...register('Bpfo', getBpfoRules(Bpfi))}
                />
            </ValidatableLabel>
            <ValidatableLabel
                label="Ball Pass Frequency of Inner Race"
                error={errors.Bpfi}
                required
            >
                <TextInput
                    placeholder="Enter ball pass frequency of inner race"
                    invalid={errors.Bpfi !== undefined}
                    {...register('Bpfi', getBpfiRules(Bpfo))}
                />
            </ValidatableLabel>
        </ActionModal>
    );
}
